import Api from '@teko/api';
import { jwtDecode } from './jwt.es.js';
import { accessTokenInterceptor } from './interceptor.es.js';
import { userManager } from './user.es.js';
import tekoOAuth from 'teko-oauth2';

let tekoOAuthPromise = null;
const LOCALSTORAGE_PREV_LOGIN_LOCATION = 'previousLocation';
const IAM_ERRORS_MAP = {
    '1019': 'Số điện thoại đã được sử dụng để đăng ký một tài khoản khác',
    '1020': 'Địa chỉ email đã được sử dụng để đăng ký một tài khoản khác.',
    '1040': 'Mã OTP không chính xác',
    '1015': 'Số điện thoại không hợp lệ',
};
class AuthService {
    constructor(iamUrl, iamClientId, iamOauthDomain) {
        this.initialize = async (scopes) => {
            if (typeof window !== 'undefined' &&
                window.location.pathname !== '/login') {
                if (!tekoOAuthPromise) {
                    tekoOAuthPromise = tekoOAuth.init({
                        clientId: this.iamClientId,
                        scopes,
                        oauthDomain: this.iamOauthDomain,
                    });
                }
                return tekoOAuthPromise
                    .then(() => {
                    tekoOAuthPromise = null;
                })
                    .catch((error) => {
                    console.error(error);
                    tekoOAuthPromise = null;
                });
            }
            else {
                return Promise.resolve(null);
            }
        };
        this.requestLogin = async (challenge) => {
            try {
                const { data } = await this.authApi.get(`/v1/requests/login/${challenge}`, {});
                return data;
            }
            catch (er) {
                console.error(er);
                if (typeof window !== 'undefined')
                    window.location.href = '/';
            }
        };
        this.login = (redirectUrl, isReturnToPreviousLocation = true) => {
            var _a, _b;
            if (isReturnToPreviousLocation)
                sessionStorage.setItem(LOCALSTORAGE_PREV_LOGIN_LOCATION, `${window.location.pathname}${window.location.search}`);
            (_b = (_a = this.tekoID) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.login(redirectUrl);
        };
        this.getAndClearBeforeLoginUrl = () => {
            const previousLocation = sessionStorage.getItem(LOCALSTORAGE_PREV_LOGIN_LOCATION);
            sessionStorage.removeItem(LOCALSTORAGE_PREV_LOGIN_LOCATION);
            return previousLocation;
        };
        this.logout = (redirectUrl) => {
            var _a, _b;
            (_b = (_a = this.tekoID) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.logout(redirectUrl);
            userManager.onLoggedOut();
        };
        this.requestLogout = async (challenge) => {
            const { data: { redirect_to: redirectTo }, } = await this.authApi.post(`/v1/requests/logout/${challenge}`, {
                challenge,
                confirmed: true,
            });
            userManager.onLoggedOut();
            if (redirectTo)
                window.location.assign(redirectTo);
        };
        this.loginSocialIAM = async (challenge, accessToken, authorizer) => {
            return await this.authApi.post('/v1/social/login', {
                challenge,
                access_token: accessToken,
                authorizer,
            });
        };
        this.sendOTP = async (challenge, phone, recaptchaToken) => {
            const { data } = await this.authApi.post('/v1/passwordless/send', Object.assign({ challenge, phone_number: phone, client_id: this.iamClientId }, (recaptchaToken && { recaptcha_token: recaptchaToken })));
            return data;
        };
        this.sendOTPUpdatePhone = async (phone) => {
            const { data } = await this.authApi.post('/v1/me/phoneNumber/verify', {
                phone_number: phone,
            });
            return data;
        };
        this.register = async (phone, name) => {
            const { data } = await this.authApi.post(`/v1/users/register`, {
                username: phone,
                password: Math.random().toString(36).slice(-16),
                name,
            });
            return data;
        };
        this.loginIAMWithPhone = async (challenge, phone, otp) => {
            const { data } = await this.authApi.post('/v1/passwordless/login', {
                challenge,
                phone_number: phone,
                code: otp,
            });
            return data;
        };
        this.activeIAMPhoneAccount = async (challenge, phone, otp) => {
            const { data } = await this.authApi.post('/v1/users/confirm', {
                challenge,
                username: phone,
                code: otp,
            });
            return data;
        };
        this.resendOTP = async (phone, mode, challenge = '', recaptchaToken) => {
            if (mode === 'login')
                return await this.sendOTP(challenge, phone, recaptchaToken);
            const { data } = await this.authApi.post('/v1/users/activation/resend', {
                username: phone,
            });
            return data;
        };
        this.refreshToken = async (refreshToken) => {
            const queries = `grant_type=refresh_token&client_id=${this.iamClientId}&refresh_token=${refreshToken}`;
            const { data: responseData } = await this.authApi.post(`${this.iamOauthDomain}/oauth/token`, queries, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
            userManager.onPartnerLoggedIn({
                accessToken: responseData.access_token,
                refreshToken: responseData.refresh_token,
                userId: jwtDecode(responseData.access_token).payload.sub,
            });
            return responseData;
        };
        this.loginWithFederation = async (idToken, authorizer) => {
            const queries = `grant_type=federated_idp&client_id=${this.iamClientId}&jwt_token=${idToken}&authorizer=${authorizer}&scope=openid profile read:permissions us ps ppm om`;
            const { data: responseData } = await this.authApi.post(`${this.iamOauthDomain}/oauth/token`, queries, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
            userManager.onPartnerLoggedIn({
                accessToken: responseData.access_token,
                refreshToken: responseData.refresh_token,
                userId: jwtDecode(responseData.access_token).payload.sub,
            });
            return responseData;
        };
        this.loginWithIBanking = async (data) => {
            const { partner_code } = data;
            let queries = `grant_type=ibanking&client_id=${this.iamClientId}&data=${data.data}&signature=${data.signature}&scope=openid profile read:permissions us ps ppm om`;
            if (partner_code) {
                queries = `${queries}&partner_code=${partner_code}`;
            }
            const { data: responseData } = await this.authApi.post(`${this.iamOauthDomain}/oauth/token`, queries, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
            const { ibanking_info } = responseData;
            userManager.onPartnerLoggedIn({
                accessToken: responseData.access_token,
                refreshToken: responseData.refresh_token,
                userId: jwtDecode(responseData.access_token).payload.sub,
                bankCode: ibanking_info.bankcode,
                phone: ibanking_info.mobile,
            });
            return responseData;
        };
        this.loginWithTerra = (loginInfo) => {
            userManager.onPartnerLoggedIn({
                userId: loginInfo.userId,
                name: loginInfo.name,
                accessToken: loginInfo.accessToken,
                phone: loginInfo.phone,
                picture: loginInfo.picture,
                email: loginInfo.email,
            });
            return userManager.getLSInfo();
        };
        this.getLoginInfo = () => {
            return userManager.getInfo();
        };
        this.isLoggedIn = () => {
            return !!userManager.getAccessToken();
        };
        this.verifyPhone = async (phoneNumber) => {
            const { data } = await this.authApi.post('/v1/me/phoneNumber/verify', {
                phone_number: phoneNumber,
            });
            return data;
        };
        this.verifyEmail = async (email) => {
            const { data } = await this.authApi.post('/v1/me/email/verify', { email });
            return data;
        };
        this.updatePhone = async (phoneNumber, otp) => {
            const { data } = await this.authApi.post('/v1/me/phoneNumber/update', {
                phone_number: phoneNumber,
                code: otp,
            });
            return data;
        };
        this.updateEmail = async (email, otp) => {
            const { data } = await this.authApi.post('/v1/me/email/update', {
                email,
                code: otp,
            });
            return data;
        };
        this.errorMsg = (err) => {
            var _a;
            const errorCode = typeof err === 'string' ? err : (_a = err === null || err === void 0 ? void 0 : err.error) === null || _a === void 0 ? void 0 : _a.code;
            return IAM_ERRORS_MAP[errorCode];
        };
        this.iamUrl = iamUrl;
        this.iamClientId = iamClientId;
        this.iamOauthDomain = iamOauthDomain;
        this.authApi = new Api(iamUrl);
        this.authApi.addInterceptor(accessTokenInterceptor);
    }
    get tekoID() {
        return window.TekoID;
    }
}

export { AuthService };
